<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'

import CampaignAside from './CampaignAside.vue'
import CampaignSummary from './CampaignSummary.vue'
import CampaignTargetStep from './CampaignPage/CampaignTargetStep.vue'
import CampaignMessageStep from './CampaignPage/CampaignMessageStep.vue'
import CampaignValidationStep from './CampaignPage/CampaignValidationStep.vue'

import { usePrefetchSurveysForTextareaVars } from '@/composables/usePrefetchSurveysForTextareaVars'
import { canProceedToValidationStep } from './utils/campaignMessageValidation'

import type { Audience, LoyaltyReward } from '@/types/types'
import type { Step } from '@/components/base/BaseStepper.vue'
import { useContentScopeEntry } from '@/composables/contentScope'

const { send } = useEvent()
const { getTagBy } = useCustomer()
const { user, resetUseStorageItems } = useLocalStorage()
const { getStoresByPermission } = useStore()
const { fetchAllRewards } = useReward()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notification = useNotification()

const messageContentRef = ref<any>(null)
const launchChoice = ref('now')
const selectedClientType = ref<any>()
const isQrCodeUnique = ref(false)
const isLoaded = ref(false)
const dateCampaign = ref<any>(null)
const launchDate = ref<any>(null)
const step = ref(1)
const timeCampaign = reactive({
  hour: null as number | null,
  minute: null as number | null
})
const messageContent = ref('')
const messageLength = ref(0)
const processing = ref(false)
const canSendSms = ref(false)
const cancelEstimateAudienceRequest = ref<any>(null)
const isFixed = ref(false)
const isStoreSenderName = ref(false)
const showVerifyNumber = ref(false)
const currentSegmentsPage = ref(1)
const lastSegmentsPage = ref<number | null>(null)
const testPhoneNumber = ref('')
const selectedClient = ref(0)
const conversionDelay = ref(7)
const { scope, isScopeModalOpen, confirmScope, askScope } = useContentScopeEntry()
const optinTargets = ref(0)
const globalCost = ref<number | null>(null)
const isLoadingEstimation = ref(false)
const selectedReward = ref<number | null>(null)

const genders = ['woman', 'man', 'unknown']
const rewardsValues = ref<Array<number | string>>([0])
const rewards = ref<LoyaltyReward[]>([])
const merchantSmsPrices = ref<any[]>([])
const countries = ref<any[]>([])
const clients = ref<any[]>([])
const channelTags = ref<any>(null)
const marketingTags = ref<any>(null)
const availableLists = ref<Audience[]>([])
const contestGames = ref<any[]>([])
const surveys = ref<any[]>([])
const tags = ref<any[]>([])

const hasMessageContentError = ref(false)

const {
  surveys: surveysForMessageStep,
  isLoading: isLoadingMessageEditorSurveys,
  loadError: messageEditorSurveysLoadError,
  fetchSurveys: fetchMessageEditorSurveys
} = usePrefetchSurveysForTextareaVars()

const selected = reactive({
  lists: [],
  excludedLists: [],
  stores: [],
  ages: [0, 100],
  checkins: [0, 5000],
  genders: ['woman', 'man', 'unknown'],
  points: [0, 5000],
  tags: [],
  firstCheckin: {
    from: '',
    to: ''
  },
  lastVisit: {
    from: '',
    to: ''
  },
  surveyQuestionChoices: [],
  contestGameIds: [],
  countryAlpha2Code: 'FR',
  frequencyTypes: []
})

const choices = reactive({
  defaultSlider: false,
  points: {
    value: [],
    max: [],
    format: (val: any) => rewardsValues.value[Math.round(val)]
  }
})

const campaignName = ref('')

const hasSurveysFeature = ref<boolean | null>(null)
const hasContestGamesFeature = ref<boolean | null>(null)

const campaignType = computed(() => route.query.type || 'sms')
const messageMaxLength = computed(() => (campaignType.value === 'sms' ? 145 : 150))

const canProceedToValidationStepFromMessage = computed(() =>
  canProceedToValidationStep({
    campaignType: campaignType.value,
    campaignName: campaignName.value,
    hasMessageContentError: hasMessageContentError.value,
    messageContent: messageContent.value,
    messageLength: messageLength.value,
    messageMaxLength: messageMaxLength.value,
    optinTargets: optinTargets.value
  })
)
const steps = computed<Step[]>(() => [
  {
    title: t('pages.campaign.campaignCreatePage.steps.01.title'),
    description: t('pages.campaign.campaignCreatePage.steps.01.description'),
    disabled: false
  },

  {
    title: t('pages.campaign.campaignCreatePage.sms.steps.02.title'),
    description: t('pages.campaign.campaignCreatePage.steps.02.description'),
    disabled: optinTargets.value && campaignName.value?.length > 0 ? false : true
  },
  {
    title: t('pages.campaign.campaignCreatePage.steps.03.title'),
    description: t('pages.campaign.campaignCreatePage.steps.03.description'),
    disabled: !canProceedToValidationStepFromMessage.value
  }
])

const mainTitle = computed(() => {
  return templateId.value
    ? `pages.campaign.campaignCreatePage.${campaignType.value}.titleWithTemplate`
    : `pages.campaign.campaignCreatePage.${campaignType.value}.title`
})
const templateId = computed(() => route.query?.templateId)
const stores = computed(() => {
  return getStoresByPermission('campaign.create').map((store) => ({
    value: store.id,
    label: store.name,
    smsName: store.smsName
  }))
})
const storeIds = computed(() => {
  return selected.stores?.length > 0 ? selected.stores?.map((store) => store.value).join(',') : ''
})

const updateQrCode = () => {
  isQrCodeUnique.value = !isQrCodeUnique.value
}

provide('qrCode', { isQrCodeUnique, updateQrCode })

watch(
  () => step.value,
  () => {
    if (step.value === 2) {
      fillDate()
    }
  }
)

watch(
  () => isLoaded.value,
  () => {
    if (isLoaded.value && document.querySelector('main')) {
      document.querySelector('main')?.addEventListener('scroll', handleScroll)
    }
  }
)

onUnmounted(() => {
  if (document.querySelector('main')) {
    document.querySelector('main')?.removeEventListener('scroll', handleScroll)
  }
})

const handleMessageContentError = (error: boolean) => {
  hasMessageContentError.value = error
}

const checkDateFormatForDatePicker = (dateString: string): boolean => {
  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/
  return dateRegex.test(dateString)
}

const handleCreation = () => {
  createCampaign()

  if (templateId.value) {
    send('segment', 'campaign.template:create:clicked', {
      templateId: templateId.value,
      merchantId: user.value.merchant.publicId,
      module: 'campaign'
    })
  }
}
const fetchRewards = async () => {
  try {
    const rewardsData = await fetchAllRewards()
    rewards.value = rewardsData
    rewardsData.forEach((arrayReward: any) => {
      if (!rewardsValues.value.includes(arrayReward.points)) {
        rewardsValues.value.push(arrayReward.points)
      }
    })
    rewardsValues.value.push('max')
    choices.points.value.push(0, rewardsValues.value.length - 1)
    choices.points.max.push(rewardsValues.value.length - 1)
  } catch {
    choices.defaultSlider = true
    choices.points.value = [0, 5000]
    choices.points.max = [5000]
  }
}

const upsertReward = (reward: LoyaltyReward) => {
  if (!reward?.id) {
    return
  }

  const rewardIndex = rewards.value.findIndex((existingReward) => existingReward.id === reward.id)

  if (rewardIndex === -1) {
    rewards.value = [...rewards.value, reward]
  } else {
    rewards.value[rewardIndex] = {
      ...rewards.value[rewardIndex],
      ...reward
    }
  }
}

const stepForward = () => {
  if (step.value >= 2 && !messageLength.value) {
    return
  }

  if (step.value === 2) {
    if (messageLength.value > messageMaxLength.value) {
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthError')
      })
      return
    }

    if (!canProceedToValidationStepFromMessage.value) {
      return
    }
  }

  if (step.value < 3 && messageLength.value <= messageMaxLength.value) {
    step.value++
  }
}

const estimateAudience = async () => {
  isLoadingEstimation.value = true
  if (cancelEstimateAudienceRequest.value !== null) {
    cancelEstimateAudienceRequest.value.cancel()
  }
  cancelEstimateAudienceRequest.value = axios.CancelToken.source()

  const params: any = {
    ageMax: selected.ages[1],
    ageMin: selected.ages[0],
    campaignType: campaignType.value,
    checkinMax: selected.checkins[1],
    checkinMin: selected.checkins[0],
    contestGameIds: selected.contestGameIds.join(','),
    convert: false,
    excludedSegmentIds: selected.excludedLists.join(','),
    genders: selected.genders.join(','),
    pointMax: selected.points[1],
    pointMin: selected.points[0],
    segmentIds: selected.lists.join(','),
    storeIds: storeIds.value,
    surveyQuestionChoiceIds:
      hasSurveysFeature.value && selected.surveyQuestionChoices ? selected.surveyQuestionChoices.join(',') : [],
    tagIds: selected.tags.join(',')
  }

  if (selected.firstCheckin.from && selected.firstCheckin.to) {
    params.firstCheckinFrom = convertDateFormat(selected.firstCheckin.from)
    params.firstCheckinTo = convertDateFormat(selected.firstCheckin.to)
  }
  if (selected.lastVisit.from && selected.lastVisit.to) {
    params.lastVisitFrom = convertDateFormat(selected.lastVisit.from)
    params.lastVisitTo = convertDateFormat(selected.lastVisit.to)
  }

  try {
    const response = await axios.get('campaigns/estimated-audience', {
      params: {
        ...params,
        withPrice: 1
      },
      cancelToken: cancelEstimateAudienceRequest.value.token
    })
    const { data } = response.data

    optinTargets.value = Number(data.optinTargets)
    globalCost.value = data.price / 100
  } catch (error: any) {
    if (axios.isCancel && axios.isCancel(error)) {
      return
    }
    notification.create('error', {
      text:
        error?.response?.data?.message || t('pages.campaign.campaignCreatePage.notifications.estimateAudience.error'),
      persist: true
    })
  } finally {
    isLoadingEstimation.value = false
  }
}

const fetchSmsPrices = async () => {
  if (merchantSmsPrices.value.length > 0) {
    return
  }

  try {
    const response = await axios.get(`/merchants/${user.value.merchant.id}/sms-prices`, {
      params: {
        isLimitedToCustomerMerchants: 1
      }
    })
    merchantSmsPrices.value = response.data.data
  } catch {
    // Handle error
  }
}

const fetchTags = () => {
  channelTags.value = getTagBy('type', 'channel')
  marketingTags.value = getTagBy('type', 'marketing')

  tags.value = [...channelTags.value, ...marketingTags.value]
}

const fetchContests = async () => {
  try {
    const response = await axios.get('/contest-games')
    contestGames.value = response.data.data
    hasContestGamesFeature.value = true
  } catch (error: any) {
    if (error?.response?.status === 402) {
      contestGames.value = []
      hasContestGamesFeature.value = false
    }
  }
}

const fetchSurveys = async () => {
  try {
    const response = await axios.get('/surveys/statistics-by-survey')
    surveys.value = response.data.data
    hasSurveysFeature.value = true
    let max = null
    surveys.value.forEach((survey) => {
      max = survey.choices.reduce((prev: any, current: any) => {
        return prev?.answersCount > current?.answersCount ? prev : current
      })
      if (max) max.isHighest = true
    })
  } catch (error: any) {
    if (error?.response?.status === 402) {
      hasSurveysFeature.value = false
    }
  }
}

const fetchData = async () => {
  fetchTags()
  await Promise.all([fetchSurveys(), fetchSegments()])
}

const handleStep = () => {
  handleScroll()
}

const handleScroll = () => {
  const rightColumnRef = document.querySelector('#rightColumn')
  if (rightColumnRef) {
    const rightColumnTop = rightColumnRef.getBoundingClientRect().top
    isFixed.value = rightColumnTop < 20
  }
}

const convertDateFormat = (date: string | null) => {
  if (date) return dayjs(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
  return null
}

const fillDate = () => {
  const date = new Date()
  const now = dayjs().format('DD/MM/YYYY')
  const minutes = date.getMinutes()

  // If it's 11pm, create programmed campaign at +1 day and 00 hour
  if (!checkDateFormatForDatePicker(dateCampaign.value)) {
    if (date.getHours() === 23) {
      dateCampaign.value = templateId.value
        ? dayjs(dateCampaign.value).add(1, 'day').format('DD/MM/YYYY')
        : dayjs().add(1, 'day').format('DD/MM/YYYY')
      timeCampaign.hour = 0
      timeCampaign.minute = minutes
    } else {
      dateCampaign.value = templateId.value ? dayjs(dateCampaign.value).add(1, 'day').format('DD/MM/YYYY') : now
      timeCampaign.hour = date.getHours() + 1
      timeCampaign.minute = minutes
    }
  }
}

const fetchClients = async () => {
  try {
    const response = await axios.get('/clients')
    clients.value = response.data.data.map((c: any) => {
      return {
        id: c.id,
        name: c.name,
        registrationNumber: c.registration_number
      }
    })
  } catch {
    // Handle error
  }
}

const fetchCanSendSms = async () => {
  try {
    const response = await axios.get(`merchants/${user.value.merchant.id}/can-send-sms`)
    canSendSms.value = response.data.data.canSendSms
  } catch {
    canSendSms.value = false
  }
}

const fetchCountries = async () => {
  try {
    const { data } = await axios.get('public/countries')
    countries.value = data.data

    const userCountry = user.value?.countryId

    if (userCountry) {
      const userCountryData = countries.value.find((country) => country.id === userCountry)
      if (userCountryData) {
        selected.countryAlpha2Code = userCountryData.alpha2Code
      }
    }
  } catch {
    // Handle error
  }
}

const trySms = async () => {
  const testPhoneNumber = user.value.phone
  if (messageLength.value > messageMaxLength.value) {
    notification.create('error', {
      text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthError'),
      timeout: 5000
    })
    return
  }

  try {
    const response = await axios.post('campaigns/try-sms', {
      phoneNumber: testPhoneNumber,
      message: messageContent.value,
      storeSenderId: isStoreSenderName.value === true ? selected.stores[0]?.value : null,
      country: selected.countryAlpha2Code,
      rewardId: selectedReward.value
    })

    if (response.data.data.isTestSent) {
      notification.create('success', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.success', {
          phoneNumber: testPhoneNumber
        }),
        timeout: 5000
      })
    } else {
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.error')
      })
    }
  } catch {
    notification.create('error', {
      text: t('pages.campaign.campaignCreatePage.notifications.test.error')
    })
  }
}

const verifyNumber = async (testNumber: string) => {
  if (processing.value) {
    return
  }

  processing.value = true
  testPhoneNumber.value = testNumber

  try {
    await axios.post('/users/request-phone-confirmation-code', {
      country: selected.countryAlpha2Code,
      phoneNumber: testNumber
    })
    showVerifyNumber.value = true
  } catch (error: any) {
    notification.create('error', {
      text: error.response.data?.message
    })
  } finally {
    processing.value = false
  }
}

const login = async () => {
  try {
    const { data } = await axios.get('auth/me')
    user.value = data.data
  } catch {
    // Handle error
  }
}

const handleSubmitCode = async (code: string) => {
  try {
    const { data } = await axios.post('/users/verify-code', {
      code: code,
      phoneNumber: testPhoneNumber.value,
      country: selected.countryAlpha2Code
    })

    if (data.data.isValid) {
      login()
    } else {
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.block.trySms.invalidCode', { code: code })
      })
    }
  } catch {
    // Handle error
  }
}

const preSubmitChecks = (): boolean => {
  const missingClient = selectedClientType.value.type !== 'each' && selectedClient.value === 0

  if (missingClient || !canSendSms.value) {
    return false
  }

  return true
}

const fetchTemplate = async () => {
  try {
    const { data } = await axios.get(`templates/${templateId.value}?module=campaign`)
    const res = data.data
    if (res.prefilledDate) {
      launchChoice.value = 'later'
      dateCampaign.value = dayjs(res.prefilledDate).utc()
    }
    messageContent.value = res.templatable.messageContent
  } catch (error: any) {
    notification.create('error', {
      text: error.response?.data?.message
    })
  }
}

const createCampaign = async () => {
  if (processing.value) return
  processing.value = true

  if (campaignType.value === 'sms' && !preSubmitChecks()) {
    processing.value = false
    return
  }

  const params = {
    type: campaignType.value,
    name: campaignName.value,
    audience: {
      ageMax: selected.ages[1],
      ageMin: selected.ages[0],
      checkinMax: selected.checkins[1],
      checkinMin: selected.checkins[0],
      contestGameIds: selected.contestGameIds,
      excludedSegmentIds: selected.excludedLists,
      firstCheckinFrom: convertDateFormat(selected.firstCheckin.from),
      firstCheckinTo: convertDateFormat(selected.firstCheckin.to),
      genders: selected.genders,
      lastVisitFrom: convertDateFormat(selected.lastVisit.from),
      lastVisitTo: convertDateFormat(selected.lastVisit.to),
      pointMax: selected.points[1],
      pointMin: selected.points[0],
      segmentIds: selected.lists,
      storeIds: selected.stores?.map((store) => store.value) ?? [],
      surveyQuestionChoiceIds: selected.surveyQuestionChoices,
      tagIds: selected.tags
    },
    isStoreSenderName: isStoreSenderName.value,
    launchDate: launchDate.value ? launchDate.value.format() : null,
    messageContent: messageContent.value,
    clientId: selectedClientType.value.type === 'specific' ? selectedClient.value : null,
    hasQrcode: isQrCodeUnique.value,
    conversionDelay: conversionDelay.value,
    rewardId: selectedReward.value,
    scope: scope.value
  }

  send('segment', launchChoice.value === 'now' ? 'campaign.launch:clicked' : 'campaign.program:clicked')

  try {
    const response = await axios.post('campaigns', params)
    notification.create('success', {
      text: t('pages.campaign.campaignCreatePage.confirm.successNotification'),
      timeout: 5000
    })
    router.push({
      name: 'CampaignDetail',
      params: { campaignId: response.data.data.id },
      query: {
        type: campaignType.value
      }
    })
  } catch (error: any) {
    const errorKey = error?.response?.data?.message

    if (error.response.status === 422) {
      if (errorKey === 'content_not_allowed') {
        resetUseStorageItems()
        await router.replace({ name: 'AccountBlocked' })
        return
      } else {
        notification.create('error', {
          text: error.response.data.message,
          persist: true
        })
        return
      }
    } else {
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.create.error'),
        persist: true
      })
    }

    router.push({
      name: 'CampaignList',
      query: {
        type: campaignType.value
      }
    })
  } finally {
    processing.value = false
  }
}

const fetchSegments = async (searchText?: string) => {
  const params = {
    page: currentSegmentsPage.value,
    pageSize: 1000,
    search: searchText
  }

  try {
    const { data } = await axios.get(`/merchants/${user.value.merchant.id}/segments`, {
      params
    })
    lastSegmentsPage.value = data.meta.last_page
    currentSegmentsPage.value = data.meta.current_page

    const formattedSegments = data.data?.map((s: Audience) => {
      return {
        id: s.id,
        name: s.name,
        type: s.type,
        frequency_type: s.frequency_type,
        is_processing: s.is_processing,
        store_ids: s.store_ids
      }
    })

    if (data.meta.current_page < 2) {
      availableLists.value = [...formattedSegments]
    } else {
      availableLists.value = [...availableLists.value, ...formattedSegments]
    }
  } catch {
    notification.create('error', {
      text: t('pages.campaign.campaignCreatePage.loading.list.error'),
      timeout: 3000
    })
  }
}

const handleLoadAudiences = (text?: string) => {
  currentSegmentsPage.value++
  if (currentSegmentsPage.value > (lastSegmentsPage.value || 0)) {
    return
  }
  fetchSegments(text)
}

const searchSegmentByName = (text?: string) => {
  currentSegmentsPage.value = 1
  fetchSegments(text)
}

const reactive_selected = ref(selected)
watch(
  reactive_selected,
  (newValue) => {
    Object.assign(selected, newValue)
  },
  { deep: true }
)

const reactive_timeCampaign = ref(timeCampaign)
watch(
  reactive_timeCampaign,
  (newValue) => {
    Object.assign(timeCampaign, newValue)
  },
  { deep: true }
)
watch(
  () => selected,
  () => {
    estimateAudience()
  },
  { deep: true, immediate: true }
)

onMounted(async () => {
  selected.stores = stores.value

  const type = campaignType.value === 'sms' ? campaignType.value.toUpperCase() : campaignType.value

  campaignName.value = t('pages.campaign.campaignCreatePage.defaultCampaignName', {
    type: type,
    date: dayjs().format('DD/MM/YYYY - HH:mm')
  })

  if (templateId.value) {
    await fetchTemplate()
  }

  await Promise.all([
    fetchData(),
    estimateAudience(),
    fetchRewards(),
    fetchContests(),
    fetchCountries(),
    fetchClients(),
    fetchCanSendSms(),
    fetchMessageEditorSurveys()
  ])
  isLoaded.value = true
  askScope()
})
</script>

<template>
  <div>
    <div v-if="isLoaded">
      <div>
        <TheDetailHeader
          :title="t(mainTitle)"
          :parent="{
            label: t(`pages.campaign.campaignPage.${campaignType}.title`),
            to: { name: 'CampaignList', query: { type: campaignType } }
          }"
        >
          <template v-if="templateId" #badge>
            <BaseChip color="yellow" :text="t('pages.survey.surveyCreatePage.labels.template')" />
          </template>
        </TheDetailHeader>

        <div class="mb-12 hidden px-4 sm:block sm:px-12">
          <BaseStepper v-model="step" :steps="steps" @update:model-value="handleStep" />
        </div>

        <div class="flex w-full flex-col gap-6 px-4 pb-6 sm:px-12 sm:pb-12 xl:grid xl:grid-cols-3">
          <section class="z-50 block xl:hidden">
            <CampaignSummary
              :campaign-name="campaignName"
              :countries="countries"
              :optin-targets="optinTargets"
              :step="step"
              :message="messageContent"
              :can-proceed-to-validation-step="canProceedToValidationStepFromMessage"
              :recipients-filters="selected"
              :tags="tags"
              :surveys="hasSurveysFeature ? surveys : false"
              :is-loading-estimation="isLoadingEstimation"
              @continue="stepForward"
            />
          </section>
          <section class="col-span-2">
            <CampaignTargetStep
              v-if="step === 1"
              v-model="reactive_selected"
              v-model:campaign-name="campaignName"
              class="w-full"
              :stores="stores"
              :genders="genders"
              :tags="tags"
              :lists="availableLists"
              :channel-tags="channelTags"
              :marketing-tags="marketingTags"
              :choices="choices"
              :rewards-values="rewardsValues"
              :merchant-id="user.merchant.id"
              :contest-games="contestGames"
              :surveys="surveys"
              @load-more-audiences="handleLoadAudiences"
              @search-audience-name="searchSegmentByName"
            />
            <CampaignMessageStep
              v-else-if="step === 2"
              v-model:selected="reactive_selected"
              v-model:message-content="messageContent"
              v-model:message-content-ref="messageContentRef"
              v-model:message-length="messageLength"
              v-model:selected-reward="selectedReward"
              v-model:conversion-delay="conversionDelay"
              :rewards="rewards"
              :surveys="surveysForMessageStep"
              :surveys-loading="isLoadingMessageEditorSurveys"
              :surveys-error="messageEditorSurveysLoadError"
              class="w-full"
              @has-message-content-error="handleMessageContentError"
              @reward-upserted="upsertReward"
            />
            <CampaignValidationStep
              v-if="step === 3"
              v-model:is-store-sender-name="isStoreSenderName"
              v-model:selected="reactive_selected"
              v-model:selected-client-type="selectedClientType"
              v-model:selected-client="selectedClient"
              v-model:launch-choice="launchChoice"
              v-model:launch-date="launchDate"
              v-model:date-campaign="dateCampaign"
              v-model:time-campaign="reactive_timeCampaign"
              v-model:conversion-delay="conversionDelay"
              :is-processing="processing"
              :stores="stores"
              :countries="countries"
              :optin-targets="optinTargets"
              :message-content="messageContent"
              :merchant-sms-prices="merchantSmsPrices"
              :clients="clients"
              :global-cost="globalCost"
              :show-verify-number="showVerifyNumber"
              :rewards="rewards"
              :selected-reward="selectedReward"
              :surveys="surveysForMessageStep"
              @on-try-sms="trySms"
              @on-fetch-sms-prices="fetchSmsPrices"
              @on-create="handleCreation"
              @on-verify-number="verifyNumber"
              @on-cancel-verify-number="showVerifyNumber = false"
              @on-send-code="handleSubmitCode"
            />
          </section>
          <CampaignAside
            class="sticky top-6 hidden h-fit xl:block"
            :campaign-name="campaignName"
            :optin-targets="optinTargets"
            :step="step"
            :recipients-filters="selected"
            :lists="availableLists"
            :tags="tags"
            :surveys="hasSurveysFeature ? surveys : false"
            :is-loading-estimation="isLoadingEstimation"
            :can-proceed-to-validation-step="canProceedToValidationStepFromMessage"
            @continue="stepForward"
          />
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
    <ContentScopeModal
      v-model:open="isScopeModalOpen"
      subject="campaign"
      @confirm="confirmScope"
      @cancel="router.push({ name: 'CampaignList', query: { type: campaignType } })"
    />
  </div>
</template>
